import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import { calculateManagerBonus } from '../../utils/bonusCalculator';

const SERVER_CONFIG = {
  MENA: { color: '#f97316' },
  FR:   { color: '#3b82f6' },
  IT:   { color: '#22c55e' },
  DE:   { color: '#a855f7' },
};

const fmtM = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000)?.toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000)?.toFixed(1) + 'K';
  return String(Math.round(n));
};

const fmtUSD = (n) => { if (!n) return '$0'; return '$' + Math.round(n)?.toLocaleString('fr-FR'); };

const formatHours = (decimal) => {
  if (!decimal) return '—';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const ServerBadge = ({ server }) => {
  const cfg = SERVER_CONFIG?.[server];
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${cfg?.color}22`, color: cfg?.color, fontFamily: 'Sora, sans-serif' }}>
      {server}
    </span>
  );
};

const LEVEL_CONFIG = {
  Junior: { color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
  Rising: { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  Senior: { color: '#FF6B00', bg: 'rgba(255,107,0,0.15)' },
  Elite:  { color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
};

const ManagerPerformances = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [managerTiktokIds, setManagerTiktokIds] = useState([]);
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  // Filters
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [serverFilter, setServerFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');

  // Data
  const [monthlyData, setMonthlyData] = useState([]);
  const [creatorsInfo, setCreatorsInfo] = useState({});
  const [incrementalRateMap, setIncrementalRateMap] = useState({});

  useEffect(() => {
    const init = async () => {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) { navigate('/login-authentication'); return; }
      setAuthUser(authUser);

      const { data: profile } = await supabase?.from('users')?.select('*')?.eq('id', authUser?.id)?.single();
      if (!profile) { navigate('/login-authentication'); return; }
      setManagerProfile(profile);
      setAuthLoading(false);

      await fetchData(authUser);
    };
    init();
  }, []);

  const fetchData = async (user) => {
    setLoading(true);
    try {
      // Get distinct periods from creator_monthly_history via RPC
      const { data: periodsData } = await supabase?.rpc('get_distinct_periods');
      const periods = periodsData?.map(p => p?.period) || [];
      setAvailablePeriods(periods);
      if (periods?.length > 0) setSelectedPeriods([periods?.[0]]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPeriods?.length === 0 || !authUser) return;
    fetchMonthlyData();
  }, [selectedPeriods, authUser]);

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const allRows = [];
      for (const period of selectedPeriods) {
        const { data: monthlyRows, error } = await supabase?.from('creator_monthly_history')?.select('tiktok_id, server, period, diamonds, live_hours, live_days, tier_p, activeness_level, activeness_bonus, rankup_bonus, incremental_bonus, total_bonus, manager_id, username, group_name, charge, cashback, is_estimated')?.eq('manager_id', authUser?.id)?.eq('period', period);
        if (error) { console.error('monthly fetch error:', error); continue; }
        allRows?.push(...(monthlyRows || []));

        // Fetch incremental_rate for this period
        if (!incrementalRateMap?.[period]) {
          const { data: rateData } = await supabase?.from('manager_rates')?.select('incremental_rate')?.eq('manager_id', authUser?.id)?.eq('period', period)?.single();
          setIncrementalRateMap(prev => ({ ...prev, [period]: rateData?.incremental_rate || 50 }));
        }
      }
      setMonthlyData(allRows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const togglePeriod = (period) => {
    setSelectedPeriods(prev =>
      prev?.includes(period) ? prev?.filter(p => p !== period) : [...prev, period]
    );
  };

  const aggregated = useMemo(() => {
    const map = {};
    monthlyData?.forEach(row => {
      if (!map?.[row?.tiktok_id]) {
        map[row.tiktok_id] = {
          tiktok_id: row?.tiktok_id,
          username: row?.username,
          server: row?.server,
          level: row?.level,
          group_name: row?.group_name,
          total_diamonds: 0,
          total_live_hours: 0,
          total_bonus: 0,
          total_bonus_net: 0,
          total_bonus_brut: 0,
          total_charge: 0,
        };
      }
      map[row.tiktok_id].total_diamonds += row?.diamonds || 0;
      map[row.tiktok_id].total_live_hours += row?.live_hours || 0;
      map[row.tiktok_id].total_bonus += parseFloat(row?.total_bonus) || 0;
      map[row.tiktok_id].total_bonus_brut += parseFloat(row?.total_bonus) || 0;
      map[row.tiktok_id].total_charge += parseFloat(row?.charge) || 0;
      const rate = incrementalRateMap?.[row?.period] || 50;
      map[row.tiktok_id].total_bonus_net += calculateManagerBonus(
        parseFloat(row?.total_bonus) || 0,
        parseFloat(row?.charge) || 0,
        parseFloat(row?.incremental_bonus) || 0,
        rate
      );
    });
    return Object.values(map);
  }, [monthlyData, incrementalRateMap]);

  const filtered = useMemo(() => {
    let list = aggregated;
    if (serverFilter) list = list?.filter(c => c?.server === serverFilter);
    if (levelFilter) list = list?.filter(c => c?.level === levelFilter);
    if (search) list = list?.filter(c => c?.username?.toLowerCase()?.includes(search?.toLowerCase()) || c?.tiktok_id?.includes(search));
    return list?.sort((a, b) => b?.total_diamonds - a?.total_diamonds);
  }, [aggregated, serverFilter, levelFilter, search]);

  const totalDiamonds = filtered?.reduce((s, c) => s + c?.total_diamonds, 0);
  const totalBonus = filtered?.reduce((s, c) => s + c?.total_bonus, 0);
  const totalHours = filtered?.reduce((s, c) => s + c?.total_live_hours, 0);
  const totalBonusNet = filtered?.reduce((s, c) => s + c?.total_bonus_net, 0);

  const PERIOD_COLORS = ['#E5202A', '#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ec4899'];

  if (authLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} managerName={managerProfile?.name} />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title={t('history.title')} />
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8 space-y-5 md:space-y-8">
          {/* Header */}
          <div>
            <h1 className="font-bold text-white mb-1" style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(18px, 4vw, 24px)' }}>{t('history.title')}</h1>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>{t('history.subtitle')}</p>
          </div>

          {/* Filters */}
          <div className="space-y-3">
            {/* Period pills — horizontally scrollable on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {availablePeriods?.map((period, idx) => {
                const isSelected = selectedPeriods?.includes(period);
                const color = PERIOD_COLORS?.[selectedPeriods?.indexOf(period) % PERIOD_COLORS?.length];
                return (
                  <button
                    key={period}
                    onClick={() => togglePeriod(period)}
                    className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold transition-all"
                    style={{
                      backgroundColor: isSelected ? `${color}22` : '#111111',
                      color: isSelected ? color : 'rgba(255,255,255,0.4)',
                      border: `1px solid ${isSelected ? color : '#1f1f1f'}`,
                      fontFamily: 'Sora, sans-serif',
                      minHeight: '44px',
                    }}
                  >
                    {period}
                  </button>
                );
              })}
            </div>

            {/* Estimated data warning */}
            {monthlyData?.some(r => r?.is_estimated) && (
              <div className="px-4 py-3 rounded-lg border" style={{ backgroundColor: 'rgba(249,115,22,0.06)', borderColor: 'rgba(249,115,22,0.2)' }}>
                <p className="text-xs font-medium" style={{ color: '#f97316' }}>
                  ⚠️ {t('performancePage.estimatedWarning')}
                </p>
              </div>
            )}

            {/* Other filters */}
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <select
                value={serverFilter}
                onChange={e => setServerFilter(e?.target?.value)}
                className="flex-shrink-0 px-3 py-2 rounded-lg text-base text-white border outline-none"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px' }}
              >
                <option value="">{t('common.allServers')}</option>
                {['MENA', 'FR', 'IT', 'DE']?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              <select
                value={levelFilter}
                onChange={e => setLevelFilter(e?.target?.value)}
                className="flex-shrink-0 px-3 py-2 rounded-lg text-base text-white border outline-none"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px' }}
              >
                <option value="">{t('common.allLevels')}</option>
                {['Junior', 'Rising', 'Senior', 'Elite']?.map(l => <option key={l} value={l}>{l}</option>)}
              </select>

              <input
                type="text"
                placeholder={t('common.search')}
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                className="flex-shrink-0 px-3 py-2 rounded-lg text-base text-white placeholder-white/30 border outline-none"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minWidth: '160px', minHeight: '44px', fontSize: '16px' }}
              />

              {(serverFilter || levelFilter || search) && (
                <button
                  onClick={() => { setServerFilter(''); setLevelFilter(''); setSearch(''); }}
                  className="flex-shrink-0 px-3 py-2 rounded-lg text-sm text-white/50 border"
                  style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px' }}
                >
                  {t('common.reset')}
                </button>
              )}
            </div>
          </div>

          {/* KPI Cards — 2x2 on mobile */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: t('history.creator'), value: filtered?.length },
              { label: t('performance.totalDiamonds'), value: fmtM(totalDiamonds), color: '#f97316' },
              { label: t('history.liveHours'), value: formatHours(totalHours), color: '#3b82f6' },
              { label: t('history.bonusNetEstime'), value: fmtUSD(totalBonusNet), color: '#22c55e' },
            ]?.map(card => (
              <div key={card?.label} className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 16px)' }}>
                <p className="text-xs text-white/40 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{card?.label}</p>
                <p className="text-2xl font-bold" style={{ color: card?.color || 'white', fontFamily: 'Sora, sans-serif' }}>{card?.value}</p>
              </div>
            ))}
          </div>

          {/* Creator Table — horizontal scroll with sticky username column */}
          <div>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="w-full" style={{ minWidth: '500px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
                      {[t('history.creator'), t('history.server'), t('history.level'), t('history.group'), t('history.diamonds'), t('history.liveHours'), t('history.tier'), t('history.bonus'), t('history.bonusBrut'), t('history.charge'), t('history.bonusNet')]?.map((h, i) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-bold text-white/40 uppercase tracking-wider whitespace-nowrap"
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            ...(i === 0 ? { position: 'sticky', left: 0, backgroundColor: '#0d0d0d', zIndex: 1 } : {}),
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 8 })?.map((_, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #1f1f1f' }}>
                          {Array.from({ length: 11 })?.map((_, j) => (
                            <td key={j} className="px-4 py-3"><div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: '80px' }} /></td>
                          ))}
                        </tr>
                      ))
                    ) : filtered?.length === 0 ? (
                      <tr><td colSpan={11} className="px-4 py-8 text-center text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('history.noCreatorFound')}</td></tr>
                    ) : filtered?.map(c => {
                      const levelCfg = LEVEL_CONFIG?.[c?.level];
                      return (
                        <tr
                          key={c?.tiktok_id}
                          onClick={() => navigate(`/manager/creators/${c?.tiktok_id}`)}
                          className="cursor-pointer transition-colors"
                          style={{ borderBottom: '1px solid #1f1f1f' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#161616'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <td className="px-4 py-3" style={{ position: 'sticky', left: 0, backgroundColor: 'inherit', zIndex: 1, maxWidth: '140px' }}>
                            <span className="text-sm text-white" style={{ fontFamily: 'Inter, sans-serif', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c?.username || '—'}</span>
                          </td>
                          <td className="px-4 py-3"><ServerBadge server={c?.server} /></td>
                          <td className="px-4 py-3">
                            {levelCfg ? (
                              <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: levelCfg?.bg, color: levelCfg?.color, fontFamily: 'Inter, sans-serif' }}>{c?.level}</span>
                            ) : <span className="text-xs text-white/30">—</span>}
                          </td>
                          <td className="px-4 py-3"><span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{c?.group_name || '—'}</span></td>
                          <td className="px-4 py-3"><span className="text-sm font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{fmtM(c?.total_diamonds)}</span></td>
                          <td className="px-4 py-3"><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>{formatHours(c?.total_live_hours)}</span></td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>—</span>
                          </td>
                          <td className="px-4 py-3"><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtUSD(c?.total_bonus)}</span></td>
                          <td className="px-4 py-3"><span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtUSD(c?.total_bonus_brut)}</span></td>
                          <td className="px-4 py-3"><span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtUSD(c?.total_charge)}</span></td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-bold" style={{ color: c?.total_bonus_net > 0 ? '#22c55e' : 'rgba(255,255,255,0.25)', fontFamily: 'Sora, sans-serif' }}>
                              {c?.total_bonus_net > 0 ? fmtUSD(c?.total_bonus_net) : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ManagerBottomNav />
    </div>
  );
};

export default ManagerPerformances;
